import { useParams } from 'react-router-dom'
import { Text, Card, Link, Separator } from '@/ui'
import { formatCurrencyFromCentsAndRound } from '@/utils/currencyHelpers'
import { BalanceSheetCategoryData } from '@/actions/reportActions'
import {
  generateLineItemLink,
  SUBCATEGORY_LABELS,
  SUBCATEGORY_TOTAL_LABELS,
} from '../helpers'
import { TypographyStyles } from '@/styles'

interface BalanceSheetCategoryProps {
  categoryData: BalanceSheetCategoryData
  totalLabel: string
  isPdf: boolean
  balanceSheetDate: string
}

export const BalanceSheetCategory = ({
  categoryData,
  totalLabel,
  isPdf,
  balanceSheetDate,
}: BalanceSheetCategoryProps) => {
  const { userId } = useParams<{ userId: string }>()

  return (
    <div className="flex flex-col gap-4">
      {categoryData.subcategories.map((subcategory, subIndex) => (
        <div key={subcategory.label}>
          {subcategory.label !== 'Equity' && (
            <div className="mb-2">
              <Text variant={TypographyStyles.H3}>
                {SUBCATEGORY_LABELS[subcategory.label] ?? subcategory.label}
              </Text>
            </div>
          )}
          {subcategory.accounts.map((account, idx) => {
            const linkUrl = generateLineItemLink(
              account,
              userId,
              balanceSheetDate
            )
            const displayLabel = /^opening\s+balance\s+equity\s*\*?\s*$/i.test(
              account.label?.trim() ?? ''
            )
              ? 'Opening Balance Equity *'
              : account.label
            return (
              <div key={account.label + idx} className="py-2">
                <div className="flex justify-between">
                  <div className="flex-1">
                    {isPdf ? (
                      <Text>{displayLabel}</Text>
                    ) : (
                      <Link href={linkUrl ?? ''} className="text-left">
                        <Text>{displayLabel}</Text>
                      </Link>
                    )}
                  </div>
                  <div className="flex-1 text-right">
                    <Text variant={TypographyStyles.H3}>
                      {formatCurrencyFromCentsAndRound(account.sumInCents, {
                        hideDecimalsIfZero: true,
                      })}
                    </Text>
                  </div>
                </div>
              </div>
            )
          })}
          <Separator />
          <div key={subcategory.label} className="py-2">
            <div className="flex justify-between">
              <div className="flex-1">
                <Text variant={TypographyStyles.H3}>
                  {SUBCATEGORY_TOTAL_LABELS[subcategory.label] ??
                    `Total ${subcategory.label}`}
                </Text>
              </div>
              <div className="flex-1 text-right">
                <Text variant={TypographyStyles.H3}>
                  {formatCurrencyFromCentsAndRound(subcategory.sumInCents, {
                    hideDecimalsIfZero: true,
                  })}
                </Text>
              </div>
            </div>
            {subIndex !== categoryData.subcategories.length - 1 && (
              <div className="h-2" />
            )}
            <div className="h-2" />
          </div>
        </div>
      ))}
      <Card className="p-4">
        <div className="flex justify-between">
          <div className="flex-1">
            <Text variant={TypographyStyles.H3}> {totalLabel} </Text>
          </div>
          <div className="flex-1 text-right">
            <Text variant={TypographyStyles.H3}>
              {formatCurrencyFromCentsAndRound(categoryData.sumInCents, {
                hideDecimalsIfZero: true,
              })}
            </Text>
          </div>
        </div>
      </Card>
    </div>
  )
}
